import {
  concat,
  curry,
  equals,
  isEmpty,
  isNil,
  not,
  pipe,
  prop,
  uniqBy,
} from "ramda";
import {
  isNotNull,
  isNotNullOrUndefined,
  isNotNullOrUndefinedOrEmpty,
} from "./function";
import { ValueOf } from "./types";

export function isObjectLiteral(object: any): object is object {
  return (
    !isNil(object) &&
    Object.prototype.toString.call(object) === "[object Object]" &&
    Object.getPrototypeOf(object) === Object.prototype
  );
}

export function hasOwnProperty(property: string, object: any) {
  return Object.prototype.hasOwnProperty.call(object, property);
}

export function hasAnyProperties(properties: string[], object: any) {
  for (let property in properties) {
    if (hasOwnProperty(property, object)) {
      return true;
    }
  }

  return false;
}

export const propIdAsString = <A extends { id: any }>(obj: A) => String(obj.id);

type UnionFromEntriesArray<T, K extends keyof T> = K extends string
  ? [K, T[K]]
  : [string, T[K]];

/**
 * Types object entries as a union of key value pairs.
 *
 * **Notes:**
 * 1. The object should have all string keys
 *     - number keys will be converted by Object.entries to "string"
 *     - Symbol keys will be ignored
 *
 * E.G:
 * ```
 * {
 *   "key": "👍",
 *   1: "👎", // converted to "string"
 *   [Symbol(5)]: "👎", // ignored
 * }
 * ```
 *
 * 2. When using this function it is essential to make sure the types
 * of the object are correct, if they are not it could cause unintended
 * consequences:
 *
 * > ... It makes sense only in the domain of type variables (i.e. T and keyof T).
 * > Once you move to the instantiated type world it degenerates because an object
 * > can (and often does) have more properties at run-time than are statically known
 * > at compile time.
 *
 * For more details see:
 * https://github.com/microsoft/TypeScript/pull/12253#issuecomment-263132208
 *
 * @returns an array with the union of [key, value] for each object key
 */
export function getEntries<T extends Readonly<{ [key: string]: any }>>(
  object: T
) {
  return Object.entries(object) as unknown as UnionFromEntriesArray<
    T,
    keyof T
  >[];
}

export const addKeyIf =
  <T>(validatorFn: (v: ValueOf<T> | null | undefined) => boolean) =>
  (
    key: keyof T,
    value: ValueOf<T> | null | undefined,
    formatValue: (v: ValueOf<T> | null | undefined) => any = (v) => v
  ) => {
    return validatorFn(value) ? { [key]: formatValue(value) } : {};
  };

export type AddKeyIfParamsTuple<T> = [
  key: keyof T,
  value: ValueOf<T> | null | undefined,
  formatValue?: (v: ValueOf<T> | null | undefined) => any
];

export const addKeyIfNotNull = <T>(...args: AddKeyIfParamsTuple<T>) =>
  addKeyIf<T>(isNotNull)(...args);

export const addKeyIfNotEmpty = <T>(...args: AddKeyIfParamsTuple<T>) =>
  addKeyIf<T>(pipe(isEmpty, not))(...args);

export const addKeyIfNotUndefined = <T>(...args: AddKeyIfParamsTuple<T>) =>
  addKeyIf<T>(pipe(equals(undefined as any), not))(...args);

export const addKeyIfNotNullOrUndefined = <T>(
  ...args: AddKeyIfParamsTuple<T>
) => addKeyIf<T>(isNotNullOrUndefined)(...args);

export const addKeyIfNotNullOrUndefinedOrEmpty = <T>(
  ...args: AddKeyIfParamsTuple<T>
) => addKeyIf<T>(isNotNullOrUndefinedOrEmpty)(...args);

/**
 * @deprecated use addKeyIfNotUndefined instead
 */
export const conditionallyAddKey = (
  key: string,
  value: any | undefined,
  formatValue: (v: any) => any = (v) => v
) => {
  return value !== undefined && !value?.isNothing
    ? { [key]: formatValue(value) }
    : {};
};

export const addKeyIfTruthy = (
  key: string,
  value: any,
  formatValue: (v: any) => any = (v) => v
) => {
  return !!value ? { [key]: formatValue(value) } : {};
};

/**
 * @description Add a key to an object if a specific condition is met.
 * @example ...(addKeyIfCondition("zoneNumber", zoneNumber, !isNewZone))
 */
export const addKeyIfCondition = (
  key: string,
  value: any,
  condition?: boolean | null,
  formatValue: (v: any) => any = (v) => v
) => {
  return Boolean(condition) ? { [key]: formatValue(value) } : {};
};

type AddKey = (<Key extends string, Value, Object>(
  key: Key,
  value: Value,
  object: Object
) => Object & { [addedKey in Key]: Value }) &
  (<Key extends string, Value>(
    key: Key,
    value: Value
  ) => <Object>(object: Object) => Object & { [addedKey in Key]: Value }) &
  (<Key extends string>(
    key: Key
  ) => <Value, Object>(
    value: Value,
    object: Object
  ) => Object & { [addedKey in Key]: Value }) &
  (<Key extends string>(
    key: Key
  ) => <Value>(
    value: Value
  ) => <Object>(object: Object) => Object & { [addedKey in Key]: Value });
export const addKey: AddKey = curry((key: string, value: any, object: any) => ({
  ...object,
  [key]: value,
})) as any;

/**
 * Merge two arrays of objects and deduplicate by id. If an object with the same id exists, the element from the first list will be used.
 * @param prev Your original array of objects.
 * @param incoming Your updated array of objects.
 * @returns A deduplicated array of objects.
 */
export const mergeDedupeById = <T extends { id: string | number }>(
  prev: T[],
  incoming: T[]
) => uniqBy(prop("id"), concat(prev, incoming));

/**
 * Function that returns true for an object with values that are all empty. Includes [], {}, '', null, undefined. Also returns true when obj itself is null or undefined.
 * @param obj An object or null/undefined
 * @returns boolean
 */
export const objectValuesAreAllEmpty = (obj: object | null | undefined) => {
  if (isNil(obj)) {
    return true;
  }
  return Object.values(obj).every((val) => isEmpty(val) || isNil(val));
};
